import React, { createContext, useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { SearchOutlined, ClearOutlined } from "@ant-design/icons";
import { Button, Col, Drawer, List, Pagination, Row, Switch, Tooltip, message, Tabs } from "antd";
import debounce from "lodash/debounce";
import * as style from "./style.less";

import { useI18n } from "../../../../../i18n";
import { ExtractSyllablesWithRegex_extractSyllablesWithRegex } from "../../api/__generated__/ExtractSyllablesWithRegex";
import {
  useCreateSyllableFromPosition,
  useDeleteSyllableFromPosition,
  useExtractSyllablesWithRegex,
} from "../../api/metadata/dynamic/syllable";
import { ExtractPhonemesWithRegex_extractPhonemesWithRegex } from "../../api/metadata/static/__generated__/ExtractPhonemesWithRegex";
import {
  useExtractPhonemesWithRegex,
  useSetPhonemeFromPosition,
  useUnsetPhonemeFromPosition,
} from "../../api/metadata/static/phoneme";
import { useSetMuteFromPosition, useUnsetMuteFromPosition } from "../../api/metadata/static/mute";
import TextArea from "antd/es/input/TextArea";

// Common types for Syllables and Phonemes
type ExtractedSyllable = ExtractSyllablesWithRegex_extractSyllablesWithRegex;
type ExtractedPhoneme = ExtractPhonemesWithRegex_extractPhonemesWithRegex;
type ExtractedUnit = ExtractedSyllable | ExtractedPhoneme;

type TabType = "syllables" | "phonemes";

// Common extractor state
type TextUnitExtractionState = {
  isOpen: boolean;
  pattern: string;
  caseSensitive: boolean;
  projectId: string;
  activeTab: TabType;
  syllables: {
    results: ExtractedSyllable[];
  };
  phonemes: {
    results: ExtractedPhoneme[];
  };
};

// Default state
const DEFAULT_TEXT_UNIT_EXTRACTION: TextUnitExtractionState = {
  isOpen: false,
  pattern: "",
  caseSensitive: false,
  projectId: "",
  activeTab: "syllables",
  syllables: {
    results: [],
  },
  phonemes: {
    results: [],
  },
};

// Commands for reducer
type TextUnitExtractionCommand =
  | { id: "SET_PATTERN"; payload: string }
  | { id: "SET_CASE_SENSITIVE"; payload: boolean }
  | { id: "SET_SYLLABLE_RESULTS"; payload: ExtractedSyllable[] }
  | { id: "SET_PHONEME_RESULTS"; payload: ExtractedPhoneme[] }
  | { id: "SET_ACTIVE_TAB"; payload: TabType }
  | { id: "OPEN" }
  | { id: "CLOSE" }
  | { id: "SET_PROJECT_ID"; payload: string };

// Context for sharing extraction state across components if needed
export const TextUnitExtractionContext = createContext<TextUnitExtractionState>(
  DEFAULT_TEXT_UNIT_EXTRACTION,
);

interface TextUnitExtractionReducerResult {
  state: TextUnitExtractionState;
  send: {
    setCaseSensitive: (caseSensitive: boolean) => void;
    toggle: () => void;
    setSyllableResults: (results: ExtractedSyllable[]) => void;
    setPhonemeResults: (results: ExtractedPhoneme[]) => void;
    setActiveTab: (tab: TabType) => void;
    open: () => void;
    close: () => void;
    setPattern: (pattern: string) => void;
    setProjectId: (projectId: string) => void;
  };
}

export function useTextUnitExtractionReducer(
  initialProjectId = "",
): TextUnitExtractionReducerResult {
  const [textUnitExtraction, sendTextUnitExtraction] = useReducer(
    (
      state: TextUnitExtractionState,
      command: TextUnitExtractionCommand,
    ): TextUnitExtractionState => {
      if (command.id === "SET_SYLLABLE_RESULTS") {
        return {
          ...state,
          syllables: {
            ...state.syllables,
            results: command.payload,
          },
        };
      }
      if (command.id === "SET_PHONEME_RESULTS") {
        return {
          ...state,
          phonemes: {
            ...state.phonemes,
            results: command.payload,
          },
        };
      }
      if (command.id === "SET_PATTERN") {
        return {
          ...state,
          pattern: command.payload,
        };
      }
      if (command.id === "SET_CASE_SENSITIVE") {
        return {
          ...state,
          caseSensitive: command.payload,
        };
      }
      if (command.id === "SET_PROJECT_ID") {
        return {
          ...state,
          projectId: command.payload,
        };
      }
      if (command.id === "SET_ACTIVE_TAB") {
        return {
          ...state,
          activeTab: command.payload,
        };
      }
      if (command.id === "OPEN") {
        return {
          ...state,
          isOpen: true,
        };
      }
      if (command.id === "CLOSE") {
        return {
          ...state,
          isOpen: false,
        };
      }
      return state;
    },
    {
      ...DEFAULT_TEXT_UNIT_EXTRACTION,
      projectId: initialProjectId,
    },
  );

  const send = useMemo(() => {
    const commands = {
      setCaseSensitive: (caseSensitive: boolean) =>
        sendTextUnitExtraction({
          id: "SET_CASE_SENSITIVE",
          payload: caseSensitive,
        }),
      toggle: () => {
        textUnitExtraction.isOpen ? commands.close() : commands.open();
      },
      setSyllableResults: (results: ExtractedSyllable[]) =>
        sendTextUnitExtraction({
          id: "SET_SYLLABLE_RESULTS",
          payload: results,
        }),
      setPhonemeResults: (results: ExtractedPhoneme[]) =>
        sendTextUnitExtraction({
          id: "SET_PHONEME_RESULTS",
          payload: results,
        }),
      setActiveTab: (tab: TabType) =>
        sendTextUnitExtraction({
          id: "SET_ACTIVE_TAB",
          payload: tab,
        }),
      open: () =>
        sendTextUnitExtraction({
          id: "OPEN",
        }),
      close: () =>
        sendTextUnitExtraction({
          id: "CLOSE",
        }),
      setPattern: (pattern: string) =>
        sendTextUnitExtraction({
          id: "SET_PATTERN",
          payload: pattern,
        }),
      setProjectId: (projectId: string) =>
        sendTextUnitExtraction({
          id: "SET_PROJECT_ID",
          payload: projectId,
        }),
    };

    return commands;
  }, [textUnitExtraction, sendTextUnitExtraction]);

  return {
    state: textUnitExtraction,
    send,
  };
}

interface TextUnitExtractorProps {
  isOpen: boolean;
  close: () => void;
  projectId: string;
  onSyllableClick?: (syllable: ExtractedSyllable) => void;
  onPhonemeClick?: (phoneme: ExtractedPhoneme) => void;
  stringVersion: number;
  initialTab?: TabType;
}

const TextUnitExtractor: React.FC<TextUnitExtractorProps> = ({
  isOpen,
  close,
  projectId,
  onSyllableClick,
  onPhonemeClick,
  stringVersion,
  initialTab = "syllables",
}) => {
  const [t] = useI18n();
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [page, setPage] = useState(1);
  const [patternQuery, setPatternQuery] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [debouncedInfos, setDebouncedInfos] = useState<[string, boolean, string]>([
    patternQuery,
    caseSensitive,
    projectId,
  ]);
  const [applyingToAll, setApplyingToAll] = useState(false);

  // Tab change handler
  const handleTabChange = (key: string) => {
    setActiveTab(key as TabType);
    setPage(1);
  };

  const handleCaseSensitiveToggle = useCallback((checked: boolean) => {
    setCaseSensitive(checked);
  }, []);

  const handleItemClick = useCallback(
    (item: ExtractedUnit) => {
      if (activeTab === "syllables" && onSyllableClick) {
        onSyllableClick(item as ExtractedSyllable);
      } else if (activeTab === "phonemes" && onPhonemeClick) {
        onPhonemeClick(item as ExtractedPhoneme);
      }
    },
    [activeTab, onSyllableClick, onPhonemeClick],
  );

  const [debouncedPattern, debouncedCaseSensitive, debouncedProjectId] = debouncedInfos;

  const debouncedChangeHandler = useMemo(
    () =>
      debounce(
        (patternQuery: string, caseSensitive: boolean, projectId: string) =>
          setDebouncedInfos([patternQuery, caseSensitive, projectId]),
        300,
      ),
    [],
  );

  const debouncedUpdate = useCallback(debouncedChangeHandler, [debouncedChangeHandler]);

  useEffect(
    () => debouncedUpdate(patternQuery, caseSensitive, projectId),
    [patternQuery, caseSensitive, projectId, debouncedUpdate],
  );

  // Use state to control when to execute the query
  const [shouldExecuteQuery, setShouldExecuteQuery] = useState(false);

  // Use the GraphQL hooks with skip parameter to prevent automatic execution
  const { data: syllableData, loading: syllableLoading } = useExtractSyllablesWithRegex(
    {
      projectId: debouncedProjectId,
      pattern: debouncedPattern,
      caseSensitive: debouncedCaseSensitive,
    },
    {
      skip: !shouldExecuteQuery || !debouncedPattern.trim() || activeTab !== "syllables",
    },
  );

  const { data: phonemeData, loading: phonemeLoading } = useExtractPhonemesWithRegex(
    {
      projectId: debouncedProjectId,
      pattern: debouncedPattern,
      caseSensitive: debouncedCaseSensitive,
    },
    {
      skip: !shouldExecuteQuery || !debouncedPattern.trim() || activeTab !== "phonemes",
    },
  );

  // Hooks for syllable operations
  const [createSyllable, { loading: creatingLoading }] = useCreateSyllableFromPosition();
  const [deleteSyllable, { loading: deletingLoading }] = useDeleteSyllableFromPosition();

  // Hooks for phoneme operations
  const [setPhoneme, { loading: settingPhonemeLoading }] = useSetPhonemeFromPosition();
  const [unsetPhoneme, { loading: unsettingPhonemeLoading }] = useUnsetPhonemeFromPosition();
  const [setMute, { loading: settingMuteLoading }] = useSetMuteFromPosition();
  const [unsetMute, { loading: unsettingMuteLoading }] = useUnsetMuteFromPosition();

  const updatePattern = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => setPatternQuery(e.target.value),
    [setPatternQuery],
  );

  const handleSearch = useCallback(() => {
    if (patternQuery.trim()) {
      setPage(1);
      setShouldExecuteQuery(true);
    }
  }, [patternQuery]);

  const clearPattern = useCallback(() => {
    setPatternQuery("");
    setShouldExecuteQuery(false);
  }, []);

  const syllableResults = useMemo(() => {
    if (!syllableData?.extractSyllablesWithRegex) {
      return [];
    }
    return [...syllableData.extractSyllablesWithRegex];
  }, [syllableData]);

  const phonemeResults = useMemo(() => {
    if (!phonemeData?.extractPhonemesWithRegex) {
      return [];
    }
    return [...phonemeData.extractPhonemesWithRegex];
  }, [phonemeData]);

  const results = useMemo(() => {
    return activeTab === "syllables" ? syllableResults : phonemeResults;
  }, [activeTab, syllableResults, phonemeResults]);

  const loading = useMemo(() => {
    return activeTab === "syllables" ? syllableLoading : phonemeLoading;
  }, [activeTab, syllableLoading, phonemeLoading]);

  const totalElements = useMemo(() => results.length || 0, [results]);

  // Reset search state when component unmounts
  useEffect(() => {
    return () => {
      setShouldExecuteQuery(false);
    };
  }, []);

  // Reset the query execution flag when the pattern changes
  useEffect(() => {
    setShouldExecuteQuery(false);
  }, [patternQuery, caseSensitive, activeTab]);

  // Reset to page 1 when new results come in or tab changes
  useEffect(() => {
    setPage(1);
  }, [results.length, activeTab]);

  const handleApplyToAll = useCallback(async () => {
    if (!results.length || !projectId) return;

    setApplyingToAll(true);
    const messageKey = `applying-${activeTab}`;
    message.loading({
      content: t(`project.${activeTab}.applyingToAll`),
      key: messageKey,
      duration: 0,
    });

    try {
      // Process all results sequentially based on active tab
      if (activeTab === "syllables") {
        // Process syllables
        for (const syllable of results as ExtractedSyllable[]) {
          const size = syllable.endUtf16 - syllable.startUtf16;

          if (syllable.removeSyllable) {
            await deleteSyllable({
              variables: {
                projectId,
                start: syllable.startUtf16,
                size,
                stringVersion,
              },
            });
          } else {
            await createSyllable({
              variables: {
                projectId,
                start: syllable.startUtf16,
                size,
                stringVersion,
              },
            });
          }
        }
      } else {
        // Process phonemes
        for (const phoneme of results as ExtractedPhoneme[]) {
          const size = phoneme.endUtf16 - phoneme.startUtf16;

          // Handle the action based on what we need to do
          if (phoneme.muting === "MUTE") {
            // Apply muting
            await setMute({
              variables: {
                projectId,
                start: phoneme.startUtf16,
                size,
                stringVersion,
              },
            });
          } else if (phoneme.muting === "UNMUTE") {
            // Remove muting
            await unsetMute({
              variables: {
                projectId,
                start: phoneme.startUtf16,
                size,
                stringVersion,
              },
            });
          } else {
            // Apply phoneme actions (add/remove)
            if (phoneme.removePhoneme) {
              await unsetPhoneme({
                variables: {
                  projectId,
                  start: phoneme.startUtf16,
                  size,
                  stringVersion,
                },
              });
            } else {
              await setPhoneme({
                variables: {
                  projectId,
                  start: phoneme.startUtf16,
                  size,
                  stringVersion,
                  kind: phoneme.phonemeKind!,
                },
              });
            }
          }
        }
      }

      message.success({
        content: t(`project.${activeTab}.applyingToAllSuccess`, {
          [`${activeTab}Count`]: results.length,
        }),
        key: messageKey,
        duration: 3,
      });
    } catch (error) {
      console.error(`Error applying ${activeTab}:`, error);
      message.error({
        content: t(`project.${activeTab}.applyError`),
        key: messageKey,
        duration: 3,
      });
    } finally {
      setApplyingToAll(false);
    }
  }, [
    activeTab,
    results,
    projectId,
    createSyllable,
    deleteSyllable,
    setPhoneme,
    unsetPhoneme,
    setMute,
    unsetMute,
    stringVersion,
    t,
  ]);

  // Helper function to determine if the Apply button should be disabled
  const isApplyButtonDisabled = useMemo(() => {
    if (!results.length) return true;

    if (activeTab === "syllables") {
      return creatingLoading || deletingLoading;
    } else {
      return (
        settingPhonemeLoading ||
        unsettingPhonemeLoading ||
        settingMuteLoading ||
        unsettingMuteLoading
      );
    }
  }, [
    activeTab,
    results,
    creatingLoading,
    deletingLoading,
    settingPhonemeLoading,
    unsettingPhonemeLoading,
    settingMuteLoading,
    unsettingMuteLoading,
  ]);

  // Get the correct placeholder text based on active tab
  const getPlaceholderText = () => {
    if (activeTab === "syllables") {
      return "(?<Syllable1>Lou)(?<Syllable2>is) (?<Nothing1>XVI)";
    } else {
      return "(?<Consonant1>L)(?<Complex1>ou)(?<Vowel1>i)(?<Mute1>s) (?<Nothing1>XVI)";
    }
  };

  // Render item based on the active tab
  const renderItem = (item: ExtractedUnit) => {
    if (activeTab === "syllables") {
      const syllable = item as ExtractedSyllable;
      return (
        <List.Item onClick={() => handleItemClick(syllable)} className={style.result}>
          <div>
            <div>
              <strong>{syllable.text}</strong>
            </div>
            <div>{syllable.context}</div>
            <div>
              <small>
                {syllable.removeSyllable
                  ? t("project.syllables.remove")
                  : t("project.syllables.add")}
              </small>
            </div>
          </div>
        </List.Item>
      );
    } else {
      const phoneme = item as ExtractedPhoneme;
      return (
        <List.Item onClick={() => handleItemClick(phoneme)} className={style.result}>
          <div>
            <div>
              <strong>{phoneme.text}</strong>
            </div>
            <div>{phoneme.context}</div>
            <div>
              <small>
                {phoneme.muting === "MUTE"
                  ? t("project.phonemes.mute")
                  : phoneme.muting === "UNMUTE"
                  ? t("project.phonemes.unmute")
                  : phoneme.removePhoneme
                  ? t("project.phonemes.remove")
                  : `${t("project.phonemes.add")} - ${phoneme.phonemeKind}`}
              </small>
            </div>
          </div>
        </List.Item>
      );
    }
  };

  return (
    <Drawer
      title={
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          items={[
            { key: "syllables", label: t("project.syllables.title") },
            { key: "phonemes", label: t("project.phonemes.title") },
          ]}
        />
      }
      placement="right"
      styles={{
        body: { padding: 0, display: "flex", flexDirection: "column" },
        footer: { padding: "10px 16px", textAlign: "right" },
      }}
      className={style.root}
      open={isOpen}
      closable={true}
      onClose={() => {
        setShouldExecuteQuery(false);
        setPatternQuery("");
        setCaseSensitive(false);
        close();
      }}
      mask={false}
      width="55em"
      footer={
        results.length > 0 ? (
          <Button
            type="primary"
            onClick={handleApplyToAll}
            loading={applyingToAll}
            disabled={isApplyButtonDisabled}>
            {t(`project.${activeTab}.applyToAll`)}
          </Button>
        ) : null
      }>
      <List
        style={{ flex: 1, overflowY: "auto" }}
        size="large"
        dataSource={results.slice((page - 1) * 20, page * 20)}
        loading={loading}
        locale={{
          emptyText:
            shouldExecuteQuery && !loading && patternQuery.trim()
              ? t(`project.${activeTab}.noResults`)
              : t(`project.${activeTab}.helpText`),
        }}
        header={
          <div>
            <Row className={style.search} justify="center" align="middle">
              <Col style={{ textAlign: "center", fontSize: "1.2em" }} span={2}>
                <SearchOutlined />
              </Col>
              <Col span={17}>
                <div style={{ position: "relative" }}>
                  <TextArea
                    style={{ padding: "0.25em" }}
                    value={patternQuery}
                    onChange={updatePattern}
                    placeholder={getPlaceholderText()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && patternQuery.trim()) {
                        handleSearch();
                      }
                    }}
                  />
                  {patternQuery && (
                    <ClearOutlined
                      style={{
                        position: "absolute",
                        right: "8px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                      }}
                      onClick={clearPattern}
                    />
                  )}
                </div>
              </Col>

              <Col span={2}>
                <Tooltip title={t(`project.${activeTab}.caseSensitivity`)}>
                  <span style={{ marginRight: "8px" }}>Aa</span>
                  <Switch
                    checked={caseSensitive}
                    onChange={handleCaseSensitiveToggle}
                    size="small"
                  />
                </Tooltip>
              </Col>
              <Col span={3}>
                <Button
                  type="primary"
                  disabled={!patternQuery.trim()}
                  onClick={handleSearch}
                  loading={loading}>
                  {t(`project.${activeTab}.searchButton`)}
                </Button>
              </Col>
            </Row>
          </div>
        }
        renderItem={renderItem}
      />
      <Pagination
        className={style.pagination}
        onChange={setPage}
        current={page}
        total={totalElements}
        pageSize={20}
        hideOnSinglePage
        responsive
        showSizeChanger={false}
        size="small"
      />
      <p className={style.nbResults}>
        {loading
          ? t(`project.${activeTab}.loading`)
          : `${results.length} ${
              results.length !== 1
                ? t(`project.${activeTab}.results`)
                : t(`project.${activeTab}.result`)
            }`}
      </p>
    </Drawer>
  );
};

export default TextUnitExtractor;
